// ─────────────────────────────────────────────────────────────────────────────
// ChatHistoryComponent
// ─────────────────────────────────────────────────────────────────────────────
// Lists past AI chat conversations with search and delete.
// ─────────────────────────────────────────────────────────────────────────────

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest, map, startWith, takeUntil } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { AppState } from 'src/app/core/app.state';
import { IAiConversationSummary } from '../../models/ai-chat.models';
import { RelativeDatePipe } from '../../pipes/relative-date.pipe';
import * as aiChatActions from '../../store/ai-chat.actions';
import * as aiChatSelectors from '../../store/ai-chat.selectors';
import { UiModule } from 'src/app/ui/ui.module';

@Component({
  selector: 'app-chat-history',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, UiModule, RelativeDatePipe],
  templateUrl: './chat-history.component.html',
  styleUrls: ['./chat-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatHistoryComponent implements OnInit, OnDestroy {

  // ── Decorated properties ───────────────────────────────────────────────────

  @Output() conversationSelected = new EventEmitter<string>();

  // ── Public properties (from store) ────────────────────────────────────────

  activeConversationId$!: Observable<string | null>;
  filteredConversations$!: Observable<IAiConversationSummary[]>;
  conversationListLoading$!: Observable<boolean>;

  // ── Public properties (local state) ───────────────────────────────────────

  searchControl = new FormControl('');

  // ── Private properties ─────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  // ── Constructor ────────────────────────────────────────────────────────────

  constructor(private store: Store<AppState>) {}

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.activeConversationId$ = this.store.select(aiChatSelectors.selectActiveConversationId);
    this.conversationListLoading$ = this.store.select(aiChatSelectors.selectConversationListLoading);

    this.filteredConversations$ = combineLatest([
      this.store.select(aiChatSelectors.selectAiChatConversationList),
      this.searchControl.valueChanges.pipe(
        startWith(''),
        map((v) => (v ?? '').toLowerCase())
      ),
    ]).pipe(
      map(([conversations, query]) =>
        query
          ? conversations.filter((c) => c.title.toLowerCase().includes(query))
          : conversations
      ),
      takeUntil(this.destroy$)
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Public methods ─────────────────────────────────────────────────────────

  selectConversation(id: string): void {
    this.conversationSelected.emit(id);
  }

  deleteConversation(event: Event, id: string): void {
    event.stopPropagation();
    this.store.dispatch(aiChatActions.DeleteConversation({ id }));
  }

  trackById(_index: number, conversation: IAiConversationSummary): string {
    return conversation.id;
  }
}
